import { useEffect, useState } from "react";
import { toast } from "sonner";
import { TrendingUp, TrendingDown, RefreshCw, Copy, Check, X, Users, ShoppingBag, DollarSign, Target, Search, Megaphone, Lightbulb, ShieldCheck, Zap, Globe, BookOpen } from "lucide-react";
import api from "@/lib/api";

const Delta = ({ pct }) => {
  if (pct === null || pct === undefined) return <span className="text-ink/40 text-xs">—</span>;
  const up = pct >= 0;
  return (
    <span className={`text-xs font-bold inline-flex items-center gap-0.5 ${up ? "text-green-600" : "text-tangerine"}`}>
      {up ? <TrendingUp className="w-3 h-3" /> : <TrendingDown className="w-3 h-3" />}{Math.abs(pct)}%
    </span>
  );
};

const Stat = ({ icon: Icon, label, value, delta, deltaLabel }) => (
  <div className="brut-card p-4">
    <span className="grid place-items-center w-9 h-9 rounded-lg border-2 border-ink bg-white mb-2"><Icon className="w-4 h-4" /></span>
    <div className="font-display font-bold text-2xl">{value}</div>
    <div className="text-xs text-ink/60 flex items-center gap-1">{label} {delta !== undefined && <><Delta pct={delta} /> <span className="text-ink/30">{deltaLabel}</span></>}</div>
  </div>
);

const EIB = ({ effort, impact }) => (
  <div className="flex gap-2 mt-2">
    {effort && <span className="tag-pill bg-white !py-0.5 text-xs">Effort: {effort}</span>}
    {impact && <span className="tag-pill bg-sun !py-0.5 text-xs">Impact: {impact}</span>}
  </div>
);

function ProposalItem({ item, onApprove, onDismiss, approveLabel = "Approve" }) {
  const dismissed = item.status === "dismissed";
  const approved = item.status === "approved";
  return (
    <div className={`brut border-ink p-4 shadow-brut ${dismissed ? "bg-slate-100 opacity-60" : approved ? "bg-green-50" : "bg-white"}`}>
      <div className="flex items-start justify-between gap-3">
        <div className="font-display font-bold">{item.title}</div>
        {approved && <span className="tag-pill bg-green-200 !py-0.5 text-xs shrink-0"><Check className="w-3 h-3" /> Approved</span>}
        {dismissed && <span className="tag-pill bg-slate-200 !py-0.5 text-xs shrink-0">Dismissed</span>}
      </div>
      {item.finding && <p className="text-sm text-ink/70 mt-1"><b>Found:</b> {item.finding}</p>}
      {item.why && <p className="text-sm text-ink/70 mt-1"><b>Why it matters:</b> {item.why}</p>}
      {item.action && <p className="text-sm text-ink mt-1"><b>Recommended:</b> {item.action}</p>}
      <EIB effort={item.effort} impact={item.impact} />
      {item.status === "proposed" && (
        <div className="flex gap-2 mt-3">
          <button onClick={onApprove} className="brut-btn !py-1.5 !px-3 bg-green-300 text-xs"><Check className="w-3.5 h-3.5" /> {approveLabel}</button>
          <button onClick={onDismiss} className="brut-btn !py-1.5 !px-3 bg-white text-xs"><X className="w-3.5 h-3.5" /> Dismiss</button>
        </div>
      )}
    </div>
  );
}

export default function GrowthTab() {
  const [reports, setReports] = useState([]);
  const [sel, setSel] = useState(0);
  const [running, setRunning] = useState(false);
  const [opt, setOpt] = useState(null);
  const [optRunning, setOptRunning] = useState(false);

  const load = () => api.get("/admin/growth/reports").then((r) => setReports(r.data)).catch(() => {});
  const loadOpt = () => api.get("/admin/optimizer/latest").then((r) => setOpt(r.data && r.data.id ? r.data : null)).catch(() => {});
  useEffect(() => { load(); loadOpt(); }, []);

  const runOpt = async () => {
    setOptRunning(true);
    try { const r = await api.post("/admin/optimizer/run"); setOpt(r.data); toast.success("Optimizer updated"); }
    catch { toast.error("Optimizer failed"); } finally { setOptRunning(false); }
  };

  const run = async () => {
    setRunning(true);
    try { await api.post("/admin/growth/run"); toast.success("New growth report ready"); setSel(0); await load(); }
    catch { toast.error("Report failed — try again"); } finally { setRunning(false); }
  };

  const rep = reports[sel];
  const setItem = async (section, index, status) => {
    await api.put(`/admin/growth/reports/${rep.id}/item`, { section, index, status });
    load();
  };
  const approveSocial = async (idx) => {
    try { await api.post(`/admin/growth/reports/${rep.id}/social/${idx}/approve`); toast.success("Added to Social content queue as a draft"); load(); }
    catch { toast.error("Failed"); }
  };
  const copy = async (t) => { try { await navigator.clipboard.writeText(t); toast.success("Copied"); } catch { toast.error("Copy failed"); } };

  return (
    <div data-testid="admin-growth">
      <div className="flex flex-wrap items-start justify-between gap-3 mb-4">
        <div>
          <h1 className="font-display font-bold text-3xl mb-1">Growth Engine</h1>
          <p className="text-ink/60">Fresh research & recommendations every 48 hours — nothing goes public without your OK.</p>
        </div>
        <div className="flex items-center gap-2">
          {reports.length > 1 && (
            <select value={sel} onChange={(e) => setSel(parseInt(e.target.value))} className="brut-input !py-2 !w-auto text-sm" data-testid="growth-report-select">
              {reports.map((r, i) => <option key={r.id} value={i}>{r.period_label}{i === 0 ? " (latest)" : ""}</option>)}
            </select>
          )}
          <button onClick={run} disabled={running} className="brut-btn !py-2 !px-4 bg-bubble text-white text-sm" data-testid="growth-run">
            <RefreshCw className={`w-4 h-4 ${running ? "animate-spin" : ""}`} /> {running ? "Researching…" : "Run report now"}
          </button>
        </div>
      </div>

      <div className="brut-card p-3 mb-6 bg-sky/20 flex items-center gap-2 text-sm">
        <ShieldCheck className="w-5 h-5 text-ink shrink-0" />
        Everything below is a <b>proposal</b>. Approve an item to green-light it. Social approvals drop into your Social content queue; SEO/site changes are implemented in preview and go live when you redeploy.
      </div>

      {/* DAILY OPTIMIZER */}
      <div className="brut-card p-5 mb-8 bg-bubble/5" data-testid="daily-optimizer">
        <div className="flex flex-wrap items-center justify-between gap-3 mb-3">
          <div className="flex items-center gap-2">
            <span className="grid place-items-center w-9 h-9 rounded-lg border-2 border-ink bg-bubble"><Zap className="w-4 h-4 text-white" /></span>
            <div>
              <h2 className="font-display font-bold text-xl">Daily Optimizer</h2>
              <p className="text-ink/60 text-xs">Learns from your analytics + the latest web trends, every day.</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <span className={`tag-pill !py-0.5 text-xs ${opt?.live_web ? "bg-green-200" : "bg-slate-200"}`}>
              <Globe className="w-3 h-3" /> {opt?.live_web ? "Live web" : "AI knowledge"}
            </span>
            <button onClick={runOpt} disabled={optRunning} className="brut-btn !py-2 !px-4 bg-bubble text-white text-sm" data-testid="optimizer-run">
              <RefreshCw className={`w-4 h-4 ${optRunning ? "animate-spin" : ""}`} /> {optRunning ? "Analyzing…" : "Run now"}
            </button>
          </div>
        </div>

        {!opt ? (
          <p className="text-ink/60 text-sm">No optimizer run yet — click <b>Run now</b>.</p>
        ) : (
          <div className="space-y-4">
            {opt.learned && (
              <div className="brut border-ink bg-sun/20 p-3 text-sm flex gap-2" data-testid="optimizer-learned">
                <BookOpen className="w-4 h-4 mt-0.5 shrink-0" /><span><b>What it learned:</b> {opt.learned}</span>
              </div>
            )}
            <div className="grid md:grid-cols-2 gap-4">
              <div>
                <div className="font-display font-bold flex items-center gap-2 mb-2"><Megaphone className="w-4 h-4 text-tangerine" /> Advertising & new customers</div>
                <div className="space-y-2">
                  {(opt.ad_recommendations || []).map((r, i) => (
                    <div key={i} className="brut border-ink bg-white p-3 text-sm">
                      <div className="font-bold">{r.title}</div>
                      <p className="text-ink/70">{r.action}</p>
                      <EIB effort={r.effort} impact={r.impact} />
                    </div>
                  ))}
                </div>
              </div>
              <div>
                <div className="font-display font-bold flex items-center gap-2 mb-2"><Target className="w-4 h-4 text-grape" /> Engagement & time on page</div>
                <div className="space-y-2">
                  {(opt.engagement_recommendations || []).map((r, i) => (
                    <div key={i} className="brut border-ink bg-white p-3 text-sm">
                      <div className="font-bold">{r.title}</div>
                      <p className="text-ink/70">{r.action}</p>
                      <EIB effort={r.effort} impact={r.impact} />
                    </div>
                  ))}
                </div>
              </div>
            </div>
            {(opt.sources || []).length > 0 && (
              <div className="text-xs text-ink/60" data-testid="optimizer-sources">
                <b>Sources:</b>{" "}
                {opt.sources.map((s, i) => (
                  <a key={i} href={s.url} target="_blank" rel="noreferrer" className="underline hover:text-bubble mr-2">{s.title?.slice(0, 40) || s.url}</a>
                ))}
              </div>
            )}
          </div>
        )}
      </div>

      {!rep ? (
        <div className="brut-card p-6 text-ink/60" data-testid="growth-empty">No report yet. Click <b>Run report now</b> to generate your first one (takes ~30s).</div>
      ) : (
        <div className="space-y-8">
          {/* STATS */}
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3" data-testid="growth-stats">
            <Stat icon={Users} label="Visitors 7d" value={rep.stats.visitors_7d} delta={rep.stats.visitors_wow_pct} deltaLabel="WoW" />
            <Stat icon={Users} label="Visitors 30d" value={rep.stats.visitors_30d} delta={rep.stats.visitors_mom_pct} deltaLabel="MoM" />
            <Stat icon={ShoppingBag} label="Orders 7d" value={rep.stats.orders_7d} delta={rep.stats.orders_wow_pct} deltaLabel="WoW" />
            <Stat icon={DollarSign} label="Revenue 7d" value={`$${rep.stats.revenue_7d}`} />
            <Stat icon={Target} label="Conversion 7d" value={`${rep.stats.conversion_7d_pct}%`} />
            <Stat icon={Users} label="Referred 30d" value={rep.stats.referred_orders_30d} />
          </div>

          {/* PERFORMANCE */}
          <section>
            <h2 className="font-display font-bold text-xl mb-3 flex items-center gap-2"><TrendingUp className="w-5 h-5 text-bubble" /> Performance analysis</h2>
            <div className="space-y-3">
              {rep.performance.map((it, i) => (
                <ProposalItem key={i} item={it} onApprove={() => setItem("performance", i, "approved")} onDismiss={() => setItem("performance", i, "dismissed")} approveLabel="Mark to do" />
              ))}
            </div>
          </section>

          {/* SEO */}
          <section>
            <h2 className="font-display font-bold text-xl mb-3 flex items-center gap-2"><Search className="w-5 h-5 text-grape" /> SEO & content opportunities</h2>
            <div className="space-y-3">
              {rep.seo.map((it, i) => (
                <ProposalItem key={i} item={it} onApprove={() => setItem("seo", i, "approved")} onDismiss={() => setItem("seo", i, "dismissed")} approveLabel="Approve (I'll implement)" />
              ))}
            </div>
          </section>

          {/* COMPETITORS */}
          <section>
            <h2 className="font-display font-bold text-xl mb-3 flex items-center gap-2"><Lightbulb className="w-5 h-5 text-tangerine" /> Competitor insights</h2>
            <div className="grid md:grid-cols-2 gap-3">
              {rep.competitors.map((c, i) => (
                <div key={i} className="brut border-ink p-4 shadow-brut bg-white">
                  <div className="font-display font-bold">{c.name}</div>
                  <p className="text-sm text-ink/70 mt-1">{c.positioning}</p>
                  <div className="flex flex-wrap gap-2 mt-2 text-xs">
                    {c.pricing && <span className="tag-pill bg-sun !py-0.5">{c.pricing}</span>}
                    {c.turnaround && <span className="tag-pill bg-white !py-0.5">{c.turnaround}</span>}
                    {(c.platforms || []).map((p) => <span key={p} className="tag-pill bg-grape/20 !py-0.5">{p}</span>)}
                  </div>
                  {c.what_works && <p className="text-sm mt-2"><b>Borrow this:</b> {c.what_works}</p>}
                </div>
              ))}
            </div>
          </section>

          {/* SOCIAL DRAFTS */}
          <section>
            <h2 className="font-display font-bold text-xl mb-3 flex items-center gap-2"><Megaphone className="w-5 h-5 text-bubble" /> Social post proposals</h2>
            <div className="grid md:grid-cols-3 gap-3">
              {rep.social_drafts.map((s, i) => (
                <div key={i} className={`brut border-ink p-4 shadow-brut ${s.status === "approved" ? "bg-green-50" : "bg-white"}`} data-testid={`growth-social-${i}`}>
                  <div className="flex items-center justify-between mb-2">
                    <span className="tag-pill bg-white capitalize">{s.platform}</span>
                    {s.status === "approved" && <span className="tag-pill bg-green-200 !py-0.5 text-xs"><Check className="w-3 h-3" /> In queue</span>}
                  </div>
                  <p className="text-sm whitespace-pre-line">{s.caption}</p>
                  <p className="text-xs text-ink/50 mt-2 break-words">{(s.hashtags || []).join(" ")}</p>
                  <div className="flex gap-2 mt-3">
                    <button onClick={() => copy(s.caption + "\n\n" + (s.hashtags || []).join(" "))} className="brut-btn !py-1.5 !px-3 bg-sky text-xs"><Copy className="w-3.5 h-3.5" /> Copy</button>
                    {s.status !== "approved" && <button onClick={() => approveSocial(i)} className="brut-btn !py-1.5 !px-3 bg-green-300 text-xs" data-testid={`growth-approve-social-${i}`}><Check className="w-3.5 h-3.5" /> Approve → queue</button>}
                  </div>
                </div>
              ))}
            </div>
          </section>
        </div>
      )}
    </div>
  );
}
